import { useContext, useMemo } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetLayerQuery } from 'state/apiSlice';
import {
  selectScenarioCapacity,
  selectScenarioDaysOfWeekInfo,
  selectScenarioEnd,
  selectScenarioName,
  selectScenarioStart,
  setScenarioIsSaving,
  useCreateScenarioMutation,
} from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectProject,
  selectRouteSelection,
  selectSegmentId,
  selectTypicalMonth,
} from 'state/workflowSlice';

import StlCreatePopup from 'features/common/StlCreatePopup/StlCreatePopup';
import { getRouteForSparseSegments } from 'features/map/utils/route_utils';
import { closureDurationHours } from 'features/workflow_detour/utils';

import { useScenarioUI } from '../hooks/useScenarioUI';
import { DEFAULT_MIN, IScenarioPopup, estimateMins } from './utils';

export default function ScenarioCreatePopup({ open, setOpen }: IScenarioPopup) {
  const dispatch = useAppDispatch();

  const closureEnd = useAppSelector(selectScenarioEnd);
  const closureStart = useAppSelector(selectScenarioStart);
  const scenarioName = useAppSelector(selectScenarioName);
  const { daysOfWeek, startDay, endDay } = useAppSelector(
    selectScenarioDaysOfWeekInfo,
  );
  const selectedSegmentId = useAppSelector(selectSegmentId);
  const routeSelection = useAppSelector(selectRouteSelection);
  const project_slug = useAppSelector(selectProject);
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const capacity = useAppSelector(selectScenarioCapacity);
  const { layer } = useContext(LayerContext);
  const [createScenario] = useCreateScenarioMutation();
  const { setScenariosMode } = useScenarioUI();
  const { currentData: layerData } = useGetLayerQuery(layer, { skip: !layer });

  const handleApply = async () => {
    let routeSegmentIds = [selectedSegmentId];
    if (routeSelection?.length > 0 && layerData) {
      routeSegmentIds = getRouteForSparseSegments(
        layerData.routing,
        routeSelection,
      );
    }
    try {
      dispatch(setScenarioIsSaving(true));
      await createScenario({
        scenario_settings: {
          scenario_name: scenarioName,
          days_of_week: daysOfWeek,
          closure_start: closureStart,
          closure_end: closureEnd,
          segments: routeSegmentIds,
          new_capacities: [capacity],
          project_slug,
          year_month: typicalMonth,
        },
        project_slug,
      })
        .unwrap()
        .then((res) => {
          setScenariosMode(res.scenario);
          dispatch(setScenarioIsSaving(false));
        });
    } catch (e) {
      dispatch(setScenarioIsSaving(false));
      console.error('Scenario creation failed', e);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const estimatedMinutes = useMemo(() => {
    if (open && closureStart && closureEnd)
      return estimateMins(
        closureDurationHours(startDay, endDay, closureStart, closureEnd),
      );
    return DEFAULT_MIN;
  }, [closureStart, closureEnd, open, startDay, endDay]);

  const minutesLabel = useMemo(
    () => `${estimatedMinutes} minute${estimatedMinutes === 1 ? '' : 's'}`,
    [estimatedMinutes],
  );
  return (
    <StlCreatePopup
      open={open}
      itemName="scenario"
      handleApply={handleApply}
      handleClose={handleClose}
      timeLabel={minutesLabel}
    />
  );
}
